// src/AdminPanel/ManageSale.js
import React, { useState, useEffect } from "react";
import { useSale } from "../contexts/SaleContext";
import toast from "../utils/toast";
import { db } from "../firebase/firebase";
import { collection, getDocs, writeBatch, doc, getDoc, setDoc } from "firebase/firestore";

// Returns today's date as a YYYY-MM-DD string (local time)
function todayStr() {
  const d = new Date();
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

function ManageSale() {
  const { sale, saleLoading, saveSale } = useSale();

  const [isActive, setIsActive] = useState(false);
  const [discountPercent, setDiscountPercent] = useState(0);
  const [label, setLabel] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [saving, setSaving] = useState(false);

  const today = todayStr();

  // Whether the sale is currently live (today is within the date window)
  const isLiveNow =
    isActive && startDate && endDate && startDate <= today && today <= endDate;

  // Admin set isActive but start date is in the future
  const isSaleScheduled =
    isActive && startDate && today < startDate;

  // Admin set isActive but end date has already passed
  const isSaleExpired =
    isActive && endDate && today > endDate;

  const isFormValid = !isActive || (
    discountPercent > 0 &&
    discountPercent <= 100 &&
    label.trim() !== "" &&
    startDate !== "" &&
    endDate !== "" &&
    startDate <= endDate
  );

  // Seed local state from Firestore on load.
  // Use sale.isActive (the stored intent) — the admin manages intent here;
  // SaleContext's effectiveIsActive handles the runtime gating everywhere else.
  useEffect(() => {
    if (!saleLoading) {
      setIsActive(sale.isActive || false);
      setDiscountPercent(sale.discountPercent || 0);
      setLabel(sale.label || "");
      setStartDate(sale.startDate || "");
      setEndDate(sale.endDate || "");
    }
  }, [saleLoading, sale]);

  // Handlers for date changes (auto-toggle logic removed as requested)
  const handleStartDateChange = (e) => {
    setStartDate(e.target.value);
  };

  const handleEndDateChange = (e) => {
    setEndDate(e.target.value);
  };

  const handleToggle = async () => {
    if (saving) return;
    const newActive = !isActive;
    
    if (newActive) {
      if (discountPercent <= 0 || discountPercent > 100) {
        toast.error("Discount must be between 1 and 100");
        return;
      }
      if (!label.trim()) {
        toast.error("Sale label is required");
        return;
      }
      if (!startDate || !endDate) {
        toast.error("Start and end dates are required");
        return;
      }
      if (startDate > endDate) {
        toast.error("Start date must be before end date");
        return;
      }
    }

    setIsActive(newActive);
    setSaving(true);
    try {
      const newDiscountPercent = Number(discountPercent);
      await saveSale({ isActive: newActive, discountPercent: newDiscountPercent, label, startDate, endDate });

      // Bulk-update all products' discounts field in Firebase,
      // but SKIP products that have a manual (per-product) discount set.
      const newDiscountLabel = newActive && newDiscountPercent > 0 ? `${newDiscountPercent}% OFF` : "";
      const snapshot = await getDocs(collection(db, "products"));
      const batch = writeBatch(db);
      snapshot.docs.forEach((productDoc) => {
        const data = productDoc.data();
        // Skip products with a manually-set per-product discount
        if (data.discountMode === "manual") return;
        batch.update(doc(db, "products", productDoc.id), { discounts: newDiscountLabel });
      });
      await batch.commit();

      // Sync hero slides' buttonText in Firebase when sale is active
      if (newDiscountLabel) {
        const siteRef = doc(db, "siteSettings", "ContentManagement");
        const siteSnap = await getDoc(siteRef);
        if (siteSnap.exists()) {
          const siteData = siteSnap.data();
          const updatedSlides = (siteData.slides || []).map((slide) => ({
            ...slide,
            heroCTAButtonText: newDiscountLabel,
          }));
          await setDoc(siteRef, { ...siteData, slides: updatedSlides }, { merge: true });
        }
      }

      toast.success(newActive ? "Sale activated and saved!" : "Sale deactivated and saved!");
    } catch (err) {
      console.error(err);
      setIsActive(!newActive); // revert state on failure
      toast.error("Failed to save sale settings");
    } finally {
      setSaving(false);
    }
  };

  if (saleLoading) {
    return (
      <div className="upload-page">
        <div className="upload-container">
          <p style={{ textAlign: "center", padding: "40px" }}>
            <i className="fa-solid fa-spinner fa-spin"></i> Loading...
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="upload-page">
      <div className="upload-container">
        <h2>Manage Sale</h2>
        <p style={{ color: "#888", marginBottom: "24px", fontSize: "14px" }}>
          Configure the site-wide sale. The discount is applied automatically to all product badges and the marquee banner.
        </p>

        {/* Live Preview */}
        <div className="sale-preview-banner" style={{
          background: isActive ? "linear-gradient(90deg, #d2222d, #ff6b35)" : "#e0e0e0",
          color: isActive ? "#fff" : "#999",
          borderRadius: "10px",
          padding: "16px 24px",
          marginBottom: "28px",
          textAlign: "center",
          fontSize: "18px",
          fontWeight: "700",
          letterSpacing: "1px",
          transition: "all 0.3s ease",
        }}>
          {isActive ? (
            <>
              <i className="fa-solid fa-tag" style={{ marginRight: "8px" }}></i>
              {label || "Special Sale"} — {discountPercent}% OFF
              {startDate && endDate && (
                <span style={{ fontSize: "13px", fontWeight: "400", marginLeft: "12px", opacity: 0.9 }}>
                  ({startDate} to {endDate})
                </span>
              )}
            </>
          ) : (
            <span><i className="fa-solid fa-tag" style={{ marginRight: "8px" }}></i>No Active Sale</span>
          )}
        </div>

        <div className="upload-form">

          {/* Toggle */}
          <div style={{
            display: "flex",
            alignItems: "center",
            gap: "14px",
            padding: "16px 20px",
            background: "#fafafa",
            border: "1px solid #e0e0e0",
            borderRadius: "10px",
            marginBottom: "20px",
          }}>
            <label className="sale-toggle-label" style={{ fontWeight: "600", fontSize: "15px", flex: 1 }}>
              <i className="fa-solid fa-percent" style={{ marginRight: "8px", color: "#d2222d" }}></i>
              Sale Active
            </label>
            <div
              className={`sale-toggle${isActive ? " on" : ""}`}
              onClick={handleToggle}
              style={{
                width: "52px",
                height: "28px",
                borderRadius: "14px",
                background: isActive ? "#d2222d" : "#ccc",
                cursor: "pointer",
                position: "relative",
                transition: "background 0.25s",
                flexShrink: 0,
              }}
            >
              <span style={{
                position: "absolute",
                top: "4px",
                left: isActive ? "26px" : "4px",
                width: "20px",
                height: "20px",
                borderRadius: "50%",
                background: "#fff",
                transition: "left 0.25s",
                boxShadow: "0 1px 4px rgba(0,0,0,0.2)",
              }} />
            </div>
            <span style={{ fontSize: "13px", color: isActive ? "#d2222d" : "#999", fontWeight: "600", minWidth: "32px" }}>
              {isActive ? "ON" : "OFF"}
            </span>
          </div>

          {/* Date status hints — shown right after the toggle */}
          {isSaleScheduled && (
            <div style={{
              background: "#e3f2fd",
              border: "1px solid #90caf9",
              borderRadius: "8px",
              padding: "10px 14px",
              marginBottom: "16px",
              fontSize: "13px",
              color: "#1565c0",
            }}>
              <i className="fa-solid fa-clock" style={{ marginRight: "6px" }}></i>
              <strong>Sale Scheduled</strong> — This sale will automatically activate on <strong>{startDate}</strong>. The toggle is saved as ON so customers see it go live on time.
            </div>
          )}
          {isSaleExpired && (
            <div style={{
              background: "#fce4ec",
              border: "1px solid #f48fb1",
              borderRadius: "8px",
              padding: "10px 14px",
              marginBottom: "16px",
              fontSize: "13px",
              color: "#b71c1c",
            }}>
              <i className="fa-solid fa-triangle-exclamation" style={{ marginRight: "6px" }}></i>
              <strong>Sale Expired</strong> — The end date (<strong>{endDate}</strong>) has passed. Update the dates or turn off the toggle and save.
            </div>
          )}
          {isLiveNow && (
            <div style={{
              background: "#e8f5e9",
              border: "1px solid #a5d6a7",
              borderRadius: "8px",
              padding: "10px 14px",
              marginBottom: "16px",
              fontSize: "13px",
              color: "#1b5e20",
            }}>
              <i className="fa-solid fa-circle-check" style={{ marginRight: "6px" }}></i>
              <strong>Sale is Live</strong> — Active from <strong>{startDate}</strong> to <strong>{endDate}</strong>.
            </div>
          )}

          {/* Discount Percent */}
          <div className="form-group">
            <label htmlFor="ms-discount">
              <i className="fa-solid fa-percent" style={{ marginRight: "6px", color: "#d2222d" }}></i>
              Discount Percentage
            </label>
            <input
              id="ms-discount"
              type="number"
              min="0"
              max="100"
              value={discountPercent}
              onChange={(e) => setDiscountPercent(e.target.value)}
              placeholder="e.g. 15"
            />
            {isActive && discountPercent > 0 && (
              <small style={{ color: "#d2222d", marginTop: "4px", display: "block" }}>
                Badge will show: <strong>{discountPercent}% OFF</strong>
              </small>
            )}
          </div>

          {/* Sale Label */}
          <div className="form-group">
            <label htmlFor="ms-label">
              <i className="fa-solid fa-tag" style={{ marginRight: "6px", color: "#d2222d" }}></i>
              Sale Label / Title
            </label>
            <input
              id="ms-label"
              type="text"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              placeholder="e.g. Summer Sale, Eid Special, Flash Sale"
            />
          </div>

          {/* Dates */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "16px" }}>
            <div className="form-group">
              <label htmlFor="ms-start">
                <i className="fa-solid fa-calendar-day" style={{ marginRight: "6px", color: "#d2222d" }}></i>
                Start Date
              </label>
              <input
                id="ms-start"
                type="date"
                value={startDate}
                onChange={handleStartDateChange}
              />
            </div>

            <div className="form-group">
              <label htmlFor="ms-end">
                <i className="fa-solid fa-calendar-xmark" style={{ marginRight: "6px", color: "#d2222d" }}></i>
                End Date
              </label>
              <input
                id="ms-end"
                type="date"
                value={endDate}
                onChange={handleEndDateChange}
              />
            </div>
          </div>

          {/* Info box */}
          <div style={{
            background: "#fff8e1",
            border: "1px solid #ffe082",
            borderRadius: "8px",
            padding: "14px 16px",
            marginBottom: "8px",
            fontSize: "13px",
            color: "#795548",
            lineHeight: "1.6",
          }}>
            <i className="fa-solid fa-circle-info" style={{ marginRight: "6px", color: "#f9a825" }}></i>
            <strong>How it works:</strong> When sale is active, the discount percentage is automatically applied to all product discount badges and the marquee banner on the homepage. The discount field in Add Product &amp; Edit Product forms is auto-filled and disabled.
          </div>

        </div>
      </div>
    </div>
  );
}

export default ManageSale;
